"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useMenu = void 0;
var _web = require("@react-hookz/web");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = require("react");
var _flyout = require("../../flyout");
var _MenuParentContext = require("../context/MenuParentContext");
/**
 * Consider a null menuItem disabled.
 */
const isMenuItemDisabled = menuItem => menuItem === null || menuItem.getAttribute("aria-disabled") === "true";
const defaultOffset = {
  distance: 8
};

/**
 * This component connects a Menu and its target.
 * The target could be a MenuItem or any able element.
 */
const useMenu = options => {
  const {
    targetRef: providedTargetRef = null,
    menuRef: providedMenuRef = null,
    isOpen,
    onOpenRequested: providedOnOpenRequested,
    onCloseRequested: providedOnCloseRequested,
    placement = "right-start",
    offset: providedOffset = defaultOffset,
    popperProps = {},
    ...rest
  } = options;
  const {
    focusMode,
    registerSubMenu,
    parent
  } = (0, _MenuParentContext.useMenuParentContext)();
  const hasParentMenu = !!parent;
  const offset = (0, _react.useMemo)(() => {
    return hasParentMenu ? {
      // If this is a submenu, and no override is passed, set the offset to 0 to account for
      // the Menu's right/left gutter.
      distance: 0,
      // Align the sub-menu's first menuitem with the parent menu's menuitem.
      skidding: -8,
      // But also allow overrides if they are provided.
      ...(providedOffset !== defaultOffset ? providedOffset : {})
    } : providedOffset;
  }, [hasParentMenu, providedOffset]);

  // Used to track which item should be focused when the menu is opened.
  const menuRefLocalCopy = (0, _react.useRef)(null);
  const targetRefLocalCopy = (0, _react.useRef)(null);
  const menuRef = (0, _lodestarUtils.useForkRef)(providedMenuRef, menuRefLocalCopy);
  const targetRef = (0, _lodestarUtils.useForkRef)(providedTargetRef, targetRefLocalCopy);
  const [focusStrategy, setFocusStrategy] = (0, _react.useState)("none");
  const [dir] = placement.split("-");
  const {
    // We don't want to use aria-owns here - we want to use aria-controls.
    targetProps: {
      "aria-owns": _0,
      ...targetProps
    },
    flyoutProps
  } = (0, _flyout.useFlyout)({
    ...rest,
    closeOnEscape: true,
    isOpen,
    targetRef,
    onCloseRequested: providedOnCloseRequested,
    placement,
    offset,
    popperProps: {
      ...popperProps,
      modifiers: [...(popperProps.modifiers || []), {
        name: "preventOverflow"
      }]
    }
  });

  // Register with the menu context to allow others to request a close on this menu.
  const previousIsOpen = (0, _web.usePrevious)(isOpen);
  (0, _react.useEffect)(() => {
    if (!previousIsOpen && isOpen) {
      const removeRegistration = registerSubMenu(providedOnCloseRequested);
      return removeRegistration;
    }
  }, [previousIsOpen, isOpen, providedOnCloseRequested, registerSubMenu]);

  // Called when something wants to open the menu.
  const onOpenRequested = (0, _react.useCallback)(() => {
    if (isMenuItemDisabled(targetRefLocalCopy.current)) {
      return;
    }
    providedOnOpenRequested();
  }, [providedOnOpenRequested]);

  // Figures out what the menu should do if there's an applicable keydown on its target element.
  const onTargetKeyDown = (0, _react.useCallback)(event => {
    if (event.defaultPrevented) {
      return;
    }
    // This maps a key to a focusStrategy for the Menu when it opens and iff it should open.
    const keyMap = {
      Enter: "first",
      " ": "first",
      ArrowUp: (dir === "top" || dir === "bottom") && "last",
      ArrowDown: (dir === "bottom" || dir === "top") && "first",
      ArrowRight: dir === "right" && "first",
      ArrowLeft: dir === "left" && "first"
    };
    const newlyOpenedMenuFocusStrategy = keyMap[event.key];
    const isTargetDisabled = isMenuItemDisabled(targetRefLocalCopy.current);
    if (newlyOpenedMenuFocusStrategy && !isTargetDisabled) {
      event.preventDefault();
      event.stopPropagation();
      setFocusStrategy(newlyOpenedMenuFocusStrategy);
      onOpenRequested();
      return;
    }
  }, [onOpenRequested, dir]);
  const onTargetMouseEnter = (0, _react.useCallback)(event => {
    // Only attach this open-on-hover functionality to sub-menus.
    if (event.defaultPrevented || !hasParentMenu) {
      return;
    }
    // If we are in a sub-menu and the user is in keyboard mode, don't open
    // the hovered item.
    if (focusMode === "keyboard") {
      return;
    }
    setFocusStrategy("none");
    onOpenRequested();
  }, [focusMode, onOpenRequested, hasParentMenu]);
  const onTargetClick = (0, _react.useCallback)(() => {
    if (!isOpen) {
      // Mouse click, don't focus anything.
      setFocusStrategy("none");
      providedOnOpenRequested();
    } else {
      providedOnCloseRequested();
    }
  }, [isOpen, providedOnCloseRequested, providedOnOpenRequested]);
  const getMenuProps = (0, _react.useCallback)(function () {
    let {
      role,
      ...rest
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return {
      ...flyoutProps,
      ...rest,
      // For some of these, we need to combine them a bit.
      onCloseRequested: () => {
        rest?.onCloseRequested?.();
        flyoutProps.onCloseRequested();
      },
      // TODO this is a bit weird...
      placement: rest?.placement || flyoutProps.placement,
      focusStrategy,
      role: "menu" || role,
      ref: menuRef
    };
  }, [flyoutProps, focusStrategy, menuRef]);
  const getTargetHTMLProps = (0, _react.useCallback)(function () {
    let {
      "aria-haspopup": ariaHasPopup,
      onClick,
      onMouseEnter,
      onKeyDown,
      // onFocus,
      ...overrides
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return {
      ...overrides,
      ...targetProps,
      onClick: (0, _lodestarUtils.callAllHandlers)(onClick, onTargetClick),
      onMouseEnter: (0, _lodestarUtils.callAllHandlers)(onMouseEnter, onTargetMouseEnter),
      onKeyDown: (0, _lodestarUtils.callAllHandlers)(onKeyDown, onTargetKeyDown),
      // onFocus: callAllHandlers(onFocus, onTargetFocus),
      "aria-haspopup": "menu" || ariaHasPopup,
      "aria-expanded": isOpen,
      "aria-controls": isOpen ? flyoutProps.id : undefined
    };
  }, [flyoutProps.id, isOpen, onTargetClick, onTargetKeyDown, onTargetMouseEnter, targetProps]);
  return {
    getMenuProps,
    getTargetHTMLProps
  };
};
exports.useMenu = useMenu;
//# sourceMappingURL=useMenu.js.map